import React, { SyntheticEvent, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  SwitchProps,
  styled,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import style from './IntegrationCard.module.css';
import CustomTab from '../../../../shared/CustomTab/CustomTab';
import CustomButton from '../../../../shared/CustomButton/CustomButton';
import zeplinColor from '../../../../../theme/Color';

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName='.Mui-focusVisible' disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#65C466',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: '#f5f5f5',
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E0E0E0',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

export interface IntegrationConfig {
  clientId: string;
  clientSecret: string;
  apiToken: string;
  homeUrl: string;
  isEnabled: boolean;
}

interface IntegrationCardProps {
  title: string;
  iconUrl?: string;
  modeValue: number;
  onModeChange: (_: SyntheticEvent, newValue: number) => void;
  devConfig: IntegrationConfig | undefined;
  prodConfig: IntegrationConfig | undefined;
  onConfigChange: (mode: 'dev' | 'prod', field: keyof IntegrationConfig, value: any) => void;
  onToggleEnabled: (mode: 'dev' | 'prod', currentValue: boolean) => void;
  onDeleteClick: () => void;
  onEditClick: () => void;
  hasUnsavedChanges?: boolean;
  onSaveChanges?: () => void;
  onDiscardChanges?: () => void;
  loading?: boolean;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  title,
  iconUrl,
  modeValue,
  onModeChange,
  devConfig,
  prodConfig,
  onConfigChange,
  onToggleEnabled,
  onDeleteClick,
  onEditClick,
  hasUnsavedChanges = false,
  onSaveChanges,
  onDiscardChanges,
  loading = false,
}) => {
  const [useSameAsDevConfig, setUseSameAsDevConfig] = useState(false);

  const handleUseSameAsDevChange = (checked: boolean) => {
    setUseSameAsDevConfig(checked);
    if (checked && devConfig) {
      // Copy all dev config values to prod
      onConfigChange('prod', 'clientId', devConfig.clientId);
      onConfigChange('prod', 'apiToken', devConfig.apiToken);
      onConfigChange('prod', 'homeUrl', devConfig.homeUrl);
      onConfigChange('prod', 'clientSecret', devConfig.clientSecret);
    }
  };

  const renderFormFields = (mode: 'dev' | 'prod', config: IntegrationConfig | undefined) => {
    if (!config) {
      return null;
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
        {mode === 'prod' && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={useSameAsDevConfig}
                  onChange={(e) => handleUseSameAsDevChange(e.target.checked)}
                  sx={{
                    color: zeplinColor.BasicBlack,
                    '&.Mui-checked': {
                      color: zeplinColor.BasicBlack,
                    },
                  }}
                />
              }
              label={
                <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                  Use same configuration as Dev mode
                </Typography>
              }
            />
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: '24px' }}>
          <TextField
            label='Client ID'
            value={config.clientId}
            onChange={(e) => onConfigChange(mode, 'clientId', e.target.value)}
            fullWidth
            variant='outlined'
            size='small'
            disabled={mode === 'prod' && useSameAsDevConfig}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
          <TextField
            label='API Token'
            value={config.apiToken}
            onChange={(e) => onConfigChange(mode, 'apiToken', e.target.value)}
            fullWidth
            variant='outlined'
            size='small'
            disabled={mode === 'prod' && useSameAsDevConfig}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: '24px' }}>
          <TextField
            label='Home URL'
            value={config.homeUrl}
            onChange={(e) => onConfigChange(mode, 'homeUrl', e.target.value)}
            fullWidth
            variant='outlined'
            size='small'
            disabled={mode === 'prod' && useSameAsDevConfig}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
          <TextField
            label='API Key'
            value={config.clientSecret}
            onChange={(e) => onConfigChange(mode, 'clientSecret', e.target.value)}
            fullWidth
            variant='outlined'
            size='small'
            disabled={mode === 'prod' && useSameAsDevConfig}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {' '}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              minHeight: '44px',
            }}
          >
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              API
            </Typography>
            <IOSSwitch
              checked={config.isEnabled}
              onChange={(e) => onToggleEnabled(mode, e.target.checked)}
            />
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              Enabled
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <CustomButton
              disabled={!hasUnsavedChanges || loading}
              style={{
                borderRadius: '8px',
                height: '40px',
                padding: '8px 16px',

                border: hasUnsavedChanges ? `1px solid ${zeplinColor.BasicBlack}` : 'none',
                backgroundColor: hasUnsavedChanges ? zeplinColor.Neutral1 : zeplinColor.Neutral5,
                '&.hover': {
                  backgroundColor: hasUnsavedChanges ? zeplinColor.Neutral1 : zeplinColor.Neutral5,
                },
              }}
              textStyle={{
                color: hasUnsavedChanges ? zeplinColor.BasicBlack : zeplinColor.Neutral8,
              }}
              text='Revert'
              variant={hasUnsavedChanges ? 'outlined' : 'contained'}
              color='secondary'
              onClick={onDiscardChanges}
            />
            <CustomButton
              disabled={!hasUnsavedChanges || loading}
              style={{
                width: '100%',
                borderRadius: '8px',
                height: '40px',
                padding: '8px 16px',
                backgroundColor: hasUnsavedChanges ? zeplinColor.Brand1 : zeplinColor.Neutral5,
                '&.hover': {
                  backgroundColor: hasUnsavedChanges ? zeplinColor.Brand1 : zeplinColor.Neutral5,
                },
              }}
              textStyle={{
                color: hasUnsavedChanges ? zeplinColor.Neutral1 : zeplinColor.Neutral8,
              }}
              variant='contained'
              color='primary'
              text={loading && hasUnsavedChanges ? 'Saving...' : 'Save'}
              onClick={onSaveChanges}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        background: '#FAFAFA',
        display: 'flex',
        flexDirection: 'column',
        opacity: 1,
        gap: '32px',
        borderRadius: '16px',
        padding: '32px',
        width: '70%',
        margin: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            // padding: '4px 16px',

            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            // '&:hover': {
            //   backgroundColor: zeplinColor.Neutral3,
            // },
            transition: 'background-color 0.2s ease',
          }}
        >
          {iconUrl && (
            <img
              src={iconUrl}
              alt={`${title} icon`}
              width={28}
              height={28}
              style={{ borderRadius: '6px', objectFit: 'cover' }}
            />
          )}
          <Typography variant='responsiveHeading2' sx={{ fontWeight: 600 }}>
            {title}
          </Typography>

          <img
            src={'/assets/edit.svg'}
            width={18}
            height={18}
            onClick={onEditClick}
            style={{
              cursor: 'pointer',

              transition: 'background-color 0.2s ease',
            }}
          />
        </Box>
        <Box
          sx={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer' }}
          onClick={onDeleteClick}
        >
          {' '}
          <img
            src={'/assets/delete-red.svg'}
            className={style['icon']}
            alt='close'
            style={{ cursor: 'pointer' }}
          />
          <Typography color={zeplinColor.Error} variant='responsiveButtonMedium'>
            Delete Integration
          </Typography>
        </Box>
      </Box>
      <CustomTab
        tabs={[
          {
            label: 'Dev Mode',
            id: 1,
            isHidden: modeValue !== 0,
            Component: renderFormFields('dev', devConfig),
          },
          {
            label: 'Prod Mode',
            id: 2,
            isHidden: modeValue !== 1,
            Component: renderFormFields('prod', prodConfig),
          },
        ]}
        handleChange={onModeChange}
        value={modeValue}
      />
    </Box>
  );
};

export default IntegrationCard;
